import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { NoSSR } from '@ui/components/NoSSR';
import { TextTooltip } from '@ui/components/TextTooltip';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { useOneClickTradingStore } from '@ui/features/OneClickTrading/store/useOneClickTradingStore';
import { useOpenPositionPairSpreadPercentage } from '@ui/features/Trade/hooks/useOpenPositionPairSpreadPercentage';
import { usePositions } from '@ui/hooks/trade/usePositions';
import { useTotalUnrealizedPnLLive } from '@ui/hooks/trade/useTotalUnrealizedPnLLive';
import { useTradeOrders } from '@ui/hooks/trade/useTradeOrders';
import { toReadableValue } from '@ui/lib/bignumber';
import { getSemanticPropsByNumber } from '@ui/utils/getSemanticProps';
import { Badge } from '@ui/lib/park/ui/badge';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { OrderType } from '@ui/types/trade';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { ReactNode, useMemo } from 'react';
import { Box, BoxProps, Flex, HStack, VStack } from 'styled-system/jsx';

import { useBorrowRates } from '../../../hooks/useBorrowRates';
import { useFromInput } from '../../../hooks/useFromInput';
import { useGetPayableTokenBalance } from '../../../hooks/useGetPayableTokenBalance';
import { useLeverageValue } from '../../../store/useLeverage';
import { useSlippageValue } from '../../../store/useSlippage';
import { useTradingPair } from '../../../store/useTradingPair';
import { formatSlippage } from '../../../utils/formatSlippage';
import { ConfirmButton } from '../ConfirmButton/ConfirmButton';
import { EntryPrice } from '../EntryPrice';
import { usePositionSize } from '../hooks/usePositionSize';
import { LiqPrice } from '../LiqPrice';
import { MarketPrice } from '../MarketPrice';
import { RealTimeFees } from '../OpenPositionFees/Fees';
import { PositionSize } from '../PositionSize';
import { useCollateralToken } from '../store/useCollateralToken';
import { useOrderType } from '../store/useOrderType';
import { ToAmount } from '../ToAmount';
import { FooterMarketStatusMessage } from './FooterMarketStatusMessage';

// TODO: update to the final external docs link
const MIN_TRADE_DURATION_LEARN_MORE_URL = 'https://leverup.gitbook.io/docs/trading/holding-trades#minimum-trade-duration';

const toReadableUsd18 = (raw: unknown): BigNumber => {
  if (raw == null) return new BigNumber(0);
  if (BigNumber.isBigNumber(raw)) return raw;

  const asString =
    typeof raw === 'string'
      ? raw
      : typeof raw === 'bigint'
      ? raw.toString()
      : String(raw);

  // If it already looks like a readable decimal string, don't treat it as fixed-point.
  if (asString.includes('.')) return new BigNumber(asString);

  try {
    return toReadableValue(BigInt(asString), USD_DECIMALS);
  } catch {
    // Best-effort fallback: treat it as a normal numeric string.
    return new BigNumber(asString);
  }
};

const toJsNumber = (raw: unknown): number => {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'bigint') return Number(raw);

return Number(String(raw));
};

const formatSecondsToMmSs = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;

  return `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
};

const MinimumTradeDurationHelp = () => {
  return (
    <TextTooltip
      content={
        <VStack gap={2} alignItems="flex-start" p={2}>
          <VStack gap={1} alignItems="flex-start">
            <Text textStyle="text.small">1. Trade duration based on Size</Text>
            <Text textStyle="text.small">
              2. No TP during minimum trade duration
            </Text>
          </VStack>
          <Link
            href={MIN_TRADE_DURATION_LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            textStyle="text.small"
            textDecoration="underline"
          >
            Learn More
          </Link>
        </VStack>
      }
      positioning={{ placement: 'top' }}
      closeDelay={500}
    >
      <Box
        textDecoration="none"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="12px"
        h="12px"
        borderRadius="999px"
        border="1px solid"
        borderColor="v2.text.subtle"
        color="v2.text.subtle"
        fontSize="10px"
        lineHeight="1"
        cursor="help"
        userSelect="none"
        aria-label="Minimum trade duration help"
      >
        ?
      </Box>
    </TextTooltip>
  );
};

const useMinimumTradeDuration = ({
  positionSizeUsd,
  holdingTimeThresholdSizeUSD,
  minHoldingSeconds,
}: {
  positionSizeUsd: BigNumber | undefined;
  holdingTimeThresholdSizeUSD: unknown;
  minHoldingSeconds: unknown;
}) => {
  return useMemo(() => {
    if (!positionSizeUsd || positionSizeUsd.isNaN()) {
      return { shouldShow: false, minHoldingSeconds: 0 };
    }

    const thresholdUsd = toReadableUsd18(holdingTimeThresholdSizeUSD);
    if (thresholdUsd.isNaN() || thresholdUsd.lte(0)) {
      return { shouldShow: false, minHoldingSeconds: 0 };
    }

    const minHoldingSecondsNum = toJsNumber(minHoldingSeconds);
    if (!minHoldingSecondsNum || minHoldingSecondsNum <= 0) {
      return { shouldShow: false, minHoldingSeconds: 0 };
    }

    return {
      shouldShow: positionSizeUsd.gte(thresholdUsd),
      minHoldingSeconds: minHoldingSecondsNum,
    };
  }, [holdingTimeThresholdSizeUSD, minHoldingSeconds, positionSizeUsd]);
};

/** V2 summary/fine-print row: muted label left, mono value right */
const SRow = ({
  label,
  children,
  h,
  variant = 'summary',
}: {
  label: ReactNode;
  children: ReactNode;
  h?: string;
  /** mockup: .summary .srow = 24px, 11.5px/500 labels, 12px mono primary values;
      .fine/.acct .srow = 22px, 11px/400 subtle labels, 11px mono muted values */
  variant?: 'summary' | 'fine';
}) => {
  const isSummary = variant === 'summary';

  return (
    <Flex
      h={h ?? (isSummary ? '26px' : '22px')}
      alignItems="center"
      justifyContent="space-between"
      gap="12px"
    >
      <Text
        fontFamily="Inter"
        fontSize={isSummary ? '11.5px' : '11px'}
        fontWeight={isSummary ? '500' : '400'}
        color={isSummary ? 'v2.text.muted' : 'v2.text.subtle'}
        display="flex"
        alignItems="center"
        gap="6px"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
      <Box
        fontFamily="Inter"
        fontSize={isSummary ? '12px' : '11px'}
        fontWeight="400"
        color={isSummary ? 'v2.text.primary' : 'v2.text.muted'}
        style={{ fontVariantNumeric: 'tabular-nums' }}
        textAlign="end"
      >
        {children}
      </Box>
    </Flex>
  );
};

/** one hairline per section — the divider IS the section boundary
    (HL/Variational pattern); rows inside stay clean */
const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Text
    textStyle="v2.sectionLabel"
    color="v2.text.subtle"
    mb="2px"
    mt="10px"
    pt="8px"
    borderTop="1px solid"
    borderTopColor="v2.border.hairline"
  >
    {children}
  </Text>
);

/** Order summary block: Position Size / Market / Entry / Liq / Fees */
const SummaryBlock = () => {
  const { t } = useTranslation();
  const leverageValue = useLeverageValue((s) => s._persistValue);
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;

  return (
    <Box
      bg="v2.bg.surface1"
      border="1px solid"
      borderColor="v2.border.default"
      borderRadius="4px"
      px="10px"
      pt="4px"
      pb="6px"
      mb="11px"
    >
      <Flex
        minH="34px"
        pt="2px"
        pb="4px"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
      >
        <NoSSR>
          <HStack gap="6px">
            <Text textStyle="v2.meta" color="v2.text.muted" whiteSpace="nowrap">
              {t('common.Position Size')}
            </Text>
            <Badge variant="leverage" size="sm">
              {leverageValue}x
            </Badge>
          </HStack>
        </NoSSR>
        <VStack gap="0" alignItems="flex-end">
          <ToAmount />
          <PositionSize />
        </VStack>
      </Flex>
      <SRow label={t('common.Market')}>
        <MarketPrice />
      </SRow>
      <SRow label={isMarket ? t('common.Entry') : t('common.Trigger')}>
        <EntryPrice />
      </SRow>
      <LiqPriceRow />
      <SRow
        label={
          <>
            {t('common.Fees')}
          </>
        }
      >
        <RealTimeFees />
      </SRow>
    </Box>
  );
};

/** LiqPrice renders its own label+value pair; wrap to keep V2 row chrome */
const LiqPriceRow = () => {
  return (
    <Flex
      h="26px"
      alignItems="center"
      justifyContent="space-between"
      gap="12px"
      css={{
        '& > p': {
          textStyle: 'v2.meta',
          color: 'v2.text.muted',
        },
        // liq price value reads as risk — short family (V2 spec)
        '& [data-cy="LiqPrice"]': {
          textStyle: 'numbers.xsmall',
          color: 'v2.short.default',
        },
      }}
    >
      <LiqPrice />
    </Flex>
  );
};

/** Fine print under the CTA: Spread / Slippage / Execution */
const FinePrint = () => {
  const { t } = useTranslation();
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;
  const slippageValue = useSlippageValue((s) => s._persistValue);
  const spreadPercentage = useOpenPositionPairSpreadPercentage({
    isClosePrice: false,
  });

  return (
    <Box mt="2" pt="6px" borderTop="1px solid" borderTopColor="v2.border.hairline">
      <SRow label={t('common.Spread', 'Spread')} variant="fine">
        <Percentage
          textStyle="numbers.xsmall"
          color="v2.text.secondary"
          value={spreadPercentage}
          opts={{ displayDecimals: 3, isTrimTrailingZero: true }}
        />
      </SRow>
      <SRow label={t('common.Slippage tolerance', 'Slippage tolerance')} variant="fine">
        {formatSlippage(slippageValue)}
      </SRow>
      <SRow label={t('common.Execution', 'Execution')} variant="fine">
        {isMarket ? t('common.Market') : t('common.Limit')}
      </SRow>
    </Box>
  );
};

/** ACCOUNT block — the order column never ends in a void (V2 spec) */
const AccountBlock = () => {
  const { t } = useTranslation();
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const tokenBalance = useGetPayableTokenBalance();
  const totalUnrealizedPnl = useTotalUnrealizedPnLLive();
  const { data: positions } = usePositions();
  const { data: orders } = useTradeOrders();
  const oneClickEnabled = useOneClickTradingStore((s) => s.enabled);

  return (
    <Box>
      <SectionLabel>{t('common.Account', 'Account')}</SectionLabel>
      <SRow label={t('common.Available balance', 'Available balance')} variant="fine">
        <Amount
          textStyle="numbers.xsmall"
          color="v2.text.secondary"
          value={tokenBalance}
          opts={{
            suffix: ` ${payToken?.displaySymbol ?? ''}`,
            saveDecimalsMode: true,
            roundingMode: BigNumber.ROUND_DOWN,
          }}
        />
      </SRow>
      <SRow label={t('common.Unrealized PnL', 'Unrealized PnL')} variant="fine">
        <AmountUsd
          textStyle="numbers.xsmall"
          color={getSemanticPropsByNumber(totalUnrealizedPnl).color}
          value={totalUnrealizedPnl}
          opts={{ isShowSign: true }}
        />
      </SRow>
      <SRow label={t('common.Positions / Orders', 'Positions / Orders')} variant="fine">
        <NoSSR>
          {positions?.length ?? 0} / {orders?.length ?? 0}
        </NoSSR>
      </SRow>
      <SRow label={t('common.Gasless (1CT)', 'Gasless (1CT)')} variant="fine">
        <NoSSR>
          <Text
            as="span"
            textStyle="numbers.xsmall"
            color={oneClickEnabled ? 'v2.long.default' : 'v2.text.subtle'}
          >
            {oneClickEnabled
              ? t('common.Enabled', 'Enabled')
              : t('common.Disabled', 'Disabled')}
          </Text>
        </NoSSR>
      </SRow>
    </Box>
  );
};

/** FEES block: open/close rates + funding 1h L/S */
const FeesBlock = () => {
  const { t } = useTranslation();
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const { longFundingRatePerHour, shortFundingRatePerHour } = useBorrowRates();
  const openFeeP = tradingPair?.feeConfig?.openFeeP;
  const closeFeeP = tradingPair?.feeConfig?.closeFeeP;

  return (
    <Box>
      <SectionLabel>{t('common.Fees')}</SectionLabel>
      <SRow label={t('common.Open', 'Open')} variant="fine">
        <Percentage
          textStyle="numbers.xsmall"
          color="v2.text.secondary"
          value={openFeeP}
          opts={{ displayDecimals: 3, isTrimTrailingZero: true }}
        />
      </SRow>
      <SRow label={t('common.Close', 'Close')} variant="fine">
        <Percentage
          textStyle="numbers.xsmall"
          color="v2.text.secondary"
          value={closeFeeP}
          opts={{ displayDecimals: 3, isTrimTrailingZero: true }}
        />
      </SRow>
      <SRow label={t('common.Funding / 1h (L/S)', 'Funding / 1h (L/S)')} variant="fine">
        <HStack gap="4px" justifyContent="flex-end">
          <Percentage
            textStyle="numbers.xsmall"
            value={longFundingRatePerHour}
            color={getSemanticPropsByNumber(longFundingRatePerHour).color}
            opts={{ isShowSign: true, displayDecimals: 4 }}
          />
          <Text as="span" color="v2.text.subtle">
            /
          </Text>
          <Percentage
            textStyle="numbers.xsmall"
            value={shortFundingRatePerHour}
            color={getSemanticPropsByNumber(shortFundingRatePerHour).color}
            opts={{ isShowSign: true, displayDecimals: 4 }}
          />
        </HStack>
      </SRow>
    </Box>
  );
};

const MinimumTradeDurationRow = () => {
  const { t } = useTranslation();
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const inputUsd = useFromInput((s) => s.input).value.usd; // fromUsd
  const positionSizeUsd = usePositionSize({ inputUsd });

  const minimumTradeDuration = useMinimumTradeDuration({
    positionSizeUsd,
    holdingTimeThresholdSizeUSD: tradingPair?.holdingTimeThresholdSizeUSD,
    minHoldingSeconds: tradingPair?.minHoldingSeconds,
  });

  if (!minimumTradeDuration.shouldShow) return null;

  return (
    <Flex
      bg="v2.bg.surface1"
      borderLeft="2px solid"
      borderLeftColor="v2.warning"
      borderRadius="2px"
      mb="2"
      p="6px 10px"
      data-cy="MinimumTradeDurationSection"
      justifyContent="space-between"
      alignItems="center"
    >
      <Text
        textStyle="v2.meta"
        color="v2.text.secondary"
        data-cy="MinimumTradeDuration"
        display="flex"
        alignItems="center"
        gap="6px"
      >
        Minimum Trade Duration <MinimumTradeDurationHelp />
      </Text>
      <Text textStyle="numbers.xsmall" color="v2.text.secondary">
        {formatSecondsToMmSs(minimumTradeDuration.minHoldingSeconds)}
      </Text>
    </Flex>
  );
};

const FooterCore = ({
  showConfirmButton,
  ...p
}: BoxProps & { showConfirmButton: boolean }) => {
  return (
    <Box zIndex={2} display="flex" flexDirection="column" {...p}>
      <SummaryBlock />
      <FooterMarketStatusMessage />
      <MinimumTradeDurationRow />
      {showConfirmButton && <ConfirmButton />}
      <FinePrint />
      <AccountBlock />
      <FeesBlock />
    </Box>
  );
};

export const Footer = (p: BoxProps) => {
  return <FooterCore showConfirmButton {...p} />;
};

export const FooterMobile = (p: BoxProps) => {
  return <FooterCore showConfirmButton={false} {...p} />;
};

export const FooterConfirmButton = () => {
  return (
    <Box>
      <ConfirmButton />
    </Box>
  );
};
